// bm-design-system: theme-toggle primitive
import * as React from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme, type Theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface ThemeOption {
  value: Theme;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const OPTIONS: ThemeOption[] = [
  { value: "light", label: "Light mode", Icon: Sun },
  { value: "dark", label: "Dark mode", Icon: Moon },
  { value: "system", label: "System theme", Icon: Monitor },
];

const SEGMENT_LABELS: Record<Theme, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

export interface ThemeToggleProps {
  block?: boolean;
  className?: string;
  /**
   * `icons` (default): compact icon radio group used by the admin/app shells.
   * `segment`: the public footer's "Theme  LIGHT | DARK | SYSTEM" text segment.
   */
  variant?: "icons" | "segment";
}

export function ThemeToggle({
  block = false,
  className,
  variant = "icons",
}: ThemeToggleProps) {
  const [theme, setTheme] = useTheme();

  if (variant === "segment") {
    return (
      <div className={cn("theme-pref", className)}>
        <span className="theme-pref-label" id="theme-pref-label">
          Theme
        </span>
        <div
          role="radiogroup"
          aria-labelledby="theme-pref-label"
          className="theme-seg"
        >
          {OPTIONS.map(({ value }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={theme === value}
              onClick={() => setTheme(value)}
            >
              {SEGMENT_LABELS[value]}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md border border-hairline bg-surface p-0.5",
        block && "w-full",
        className,
      )}
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const isActive = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            title={label}
            onClick={() => setTheme(value)}
            className={cn(
              "inline-flex h-7 cursor-pointer items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              block ? "flex-1" : "w-7",
              isActive
                ? "bg-page text-ink-display"
                : "text-ink-muted hover:text-ink-display",
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}
